import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { RECIPES } from '../../data/recipes';
import { Recipe } from '../../schemas/recipe';
import { RecipeModal } from './RecipeModal';
import { FEATURED_RECIPE_ID } from '../../data/fullRecipes';

/**
 * Recipes grouped by meal occasion — the "goes with what you already eat"
 * spine. Ids map to entries in recipes.json; any recipe not listed here
 * falls into an "Anytime" group so nothing silently drops if the data changes.
 */
const DAYPARTS: { label: string; ids: string[] }[] = [
  { label: 'Breakfast', ids: ['smoothie', 'breakfast-sandwich', 'egg-patty', 'fufu-pancake', 'fufu-scramble'] },
  { label: 'Lunch', ids: ['jelly-sandwich', 'triscuit', 'charcuterie'] },
  { label: 'Dinner', ids: ['fillet', 'dumplings', 'relish'] },
  { label: 'Dessert', ids: ['cookies'] },
];

/**
 * Cookbook grid of recipe cards, grouped by daypart.
 * Click any card to open the recipe modal.
 *
 * Deep-linking: `/recipes/<id>` auto-opens that recipe's modal. Closing it
 * navigates back to `/recipes` (replace, so Back doesn't reopen the modal).
 * Was `#recipes/<id>` until the router landed on 2026-08-30.
 */
export function RecipesSection() {
  const { recipeId } = useParams<{ recipeId: string }>();
  const navigate = useNavigate();
  const [selected, setSelected] = useState<Recipe | null>(
    () => (recipeId ? RECIPES.find((r) => r.id === recipeId) ?? null : null),
  );

  // React to the route param changing (e.g. a WaysToEat card clicked while
  // already on /recipes).
  useEffect(() => {
    if (!recipeId) return;
    const recipe = RECIPES.find((r) => r.id === recipeId);
    if (recipe) setSelected(recipe);
  }, [recipeId]);

  const handleClose = useCallback(() => {
    setSelected(null);
    if (recipeId) navigate('/recipes', { replace: true });
  }, [recipeId, navigate]);

  // The featured recipe gets its own block above the cookbook, so it is held
  // out of the grid rather than appearing twice.
  const featured = RECIPES.find((r) => r.id === FEATURED_RECIPE_ID) ?? null;

  const mapped = new Set(DAYPARTS.flatMap((d) => d.ids));
  const leftovers = RECIPES.filter((r) => !mapped.has(r.id) && r.id !== FEATURED_RECIPE_ID);
  const groups = [
    ...DAYPARTS.map((d) => ({
      label: d.label,
      recipes: d.ids
        .map((id) => RECIPES.find((r) => r.id === id))
        .filter((r): r is Recipe => Boolean(r)),
    })),
    ...(leftovers.length ? [{ label: 'Anytime', recipes: leftovers }] : []),
  ].filter((g) => g.recipes.length > 0);

  const daypartLabelStyle: React.CSSProperties = {
    fontFamily: "'JetBrains Mono', monospace",
    fontSize: '0.8rem',
    letterSpacing: '0.18em',
    textTransform: 'uppercase',
    color: '#C9A24B',
    margin: '2.75rem 0 1.25rem',
  };

  const renderCard = (r: Recipe) => (
    <button
      key={r.id}
      type="button"
      className="recipe-card recipe-card-clickable"
      onClick={() => setSelected(r)}
      aria-label={`View recipe: ${r.name}`}
    >
      <div className="recipe-card-visual">
        {r.photo ? (
          <img
            src={`${process.env.PUBLIC_URL}${r.photo}`}
            alt={r.name}
            loading="lazy"
            className="recipe-card-photo"
          />
        ) : (
          <span className="recipe-card-emoji">{r.emoji}</span>
        )}
        <span className="recipe-card-chapter">{r.chapter}</span>
      </div>
      <div className="recipe-card-body">
        <h3 className="recipe-card-title">{r.name}</h3>
        <p className="recipe-card-desc">{r.description}</p>
        <div className="recipe-card-meta">
          {r.tags.map((t) => (
            <span key={t} className="recipe-meta-tag">{t}</span>
          ))}
        </div>
      </div>
    </button>
  );

  return (
    <section className="section fade-in" style={{ paddingTop: '2rem' }}>
      {featured && (
        <div className="featured-recipe">
          <div className="section-label">Bake with the flour</div>
          <h2 style={{ marginTop: '0.35rem' }}>{featured.name}</h2>
          <div className="featured-recipe-inner">
            {featured.photo && (
              <button
                type="button"
                className="featured-recipe-photo"
                onClick={() => setSelected(featured)}
                aria-label={`View recipe: ${featured.name}`}
              >
                <img
                  src={`${process.env.PUBLIC_URL}${featured.photo}`}
                  alt="A glass pan of TrueNooch™ deep fudge brownies on a red trivet, one square being lifted out on a fish spatula, with cut brownies and a paper-wrapped Round on the parchment beside it."
                  loading="eager"
                />
              </button>
            )}
            <div className="featured-recipe-words">
              <p>{featured.description}</p>
              <div className="recipe-card-meta" style={{ margin: '0 0 1.25rem' }}>
                {featured.tags.map((t) => (
                  <span key={t} className="recipe-meta-tag">{t}</span>
                ))}
              </div>
              <button
                type="button"
                className="featured-recipe-cta"
                onClick={() => setSelected(featured)}
              >
                Open the full recipe
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="section-label">The Cookbook</div>
      <h2>Every meal, covered.</h2>
      <p>
        Tap any card for the full recipe.
      </p>
      {groups.map((g) => (
        <div key={g.label}>
          <div style={daypartLabelStyle}>{g.label}</div>
          <div className="recipes-grid">{g.recipes.map(renderCard)}</div>
        </div>
      ))}
      <RecipeModal recipe={selected} onClose={handleClose} />
    </section>
  );
}
